import { useState } from 'react';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import { usePersona } from '@/app/providers';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { fmeaActionApproval } from '@/versions/v1/shared/FmeaLearningEngine';
import { agentApproverLabel, approvedPillText, createApprovalRecord, type AgentApprovalRecord, type AgentDecisionKind } from './AgentInvestigation.transform';
import { ActionGateControls } from './ActionGateControls';
import { ActorChip } from './PipelineStrip';
import { HandoffPanel } from './HandoffRegion';
import { GATE_APPROVED_SURFACE, GATE_PRIMARY_BUTTON, gatePillClass } from './gate-ui';
import type { stopActionRead } from './StopAction.transform';
import type { HandoffGateRead } from './HandoffRegion.transform';

/** Keeps a work-order id (`WO-441`) on one line wherever its sentence wraps. */
function NoBreakIds({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\bWO-\d+\b)/).map((part, index) =>
        index % 2 === 1 ? <span key={index} className="whitespace-nowrap">{part}</span> : part,
      )}
    </>
  );
}

/** The comp's caps kicker (`Before release`, `Stop`). */
const KICKER = 'text-[10px] font-semibold uppercase tracking-[0.07em] text-muted';

/**
 * W3d: the Maintenance handoff column (equipment-beat comp Handoff A). The
 * checks before release, then the one decision object: the draft work order
 * with W3c's gate (Maintenance's seat, prefilled editable reason, Modify and
 * Reject start empty), and who decides the stop. Approval behaviour is W3c's.
 */
export function StopAction({ caseId, read, handoff }: {
  caseId: string;
  read: NonNullable<ReturnType<typeof stopActionRead>>;
  handoff: HandoffGateRead;
}) {
  const { persona } = usePersona();
  const { can } = useRole();
  const { state } = useScenario();
  const advisory = useAdvisory();
  const [reason, setReason] = useState(read.humanCheck);
  const [decision, setDecision] = useState<AgentApprovalRecord | null>(null);
  const approval = fmeaActionApproval(advisory.fmeaLearning, caseId);
  const canDecide = can('action:approve') && agentApproverLabel(persona) === read.owner;
  const decide = (kind: AgentDecisionKind) => {
    if (!canDecide) return;
    const record = createApprovalRecord({ caseId, causeId: read.causeId, kind, reason, approver: agentApproverLabel(persona), at: state.cursor });
    if (!record) return;
    setDecision(record);
    if (kind === 'approved') advisory.recordActionApproval(caseId, read.causeId, undefined, record.reason);
  };
  return (
    <Commentable name="AdvisoryCasePage.MaintenanceHandoff">
      <section id="walkthrough-bearing-stop" data-case-section="action" className="h-full min-w-0">
        <HandoffPanel title="Maintenance handoff" actions={<ActorChip actor="Human">{handoff.owner}</ActorChip>}>
          {handoff.checks.length > 0 ? (
            <div data-release-checks>
              <p className={KICKER}>Before release</p>
              <ol className="divide-y divide-[var(--color-divider)] tabular-nums">
                {handoff.checks.map((check) => (
                  <li key={`${check.at}-${check.lead}`} className="grid grid-cols-[2.75rem_minmax(0,1fr)] gap-x-2 py-1 text-[12px] leading-[1.3] text-ink-secondary first:pt-1 last:pb-0" data-release-check={check.lead}>
                    <span className="whitespace-nowrap pt-px font-mono text-[11.5px] text-muted" data-release-check-at>{check.at}</span>
                    <span>
                      <b className="font-semibold text-ink-primary">{check.lead}</b>{check.detail}
                      {check.basis ? <span className="mt-px block text-[11.5px] text-muted">{check.basis}</span> : null}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
          <div
            className={cn(
              'mt-2 flex flex-col gap-1 rounded-[14px] border px-3.5 py-2 transition-colors duration-300',
              approval ? GATE_APPROVED_SURFACE : 'border-[var(--hairline-strong)] bg-[var(--surface-2)]',
            )}
            data-approval-gate={approval ? 'approved' : decision?.kind ?? 'draft'}
          >
            <span className={cn(gatePillClass(approval ? 'approved' : 'draft'), 'self-start')} data-action-gate-pill>
              {approval ? approvedPillText(approval.at, approval.by) : 'Draft — not live until approved'}
            </span>
            <p className="text-[13px] font-semibold leading-snug text-ink-primary" data-agent-action-lead>
              <NoBreakIds text={approval ? handoff.approvedLead : handoff.lead} />
            </p>
            <dl className="grid grid-cols-[5.25rem_minmax(0,1fr)] text-[12px] leading-snug tabular-nums" data-work-order>
              {handoff.fields.map((field, index) => (
                <div key={field.label} className={cn('contents', index > 0 ? '[&>*]:border-t [&>*]:border-[var(--color-divider)]' : undefined)}>
                  <dt className={cn(KICKER, 'py-0.5 pt-[0.25rem]')}>{field.label}</dt>
                  <dd className="min-w-0 py-0.5 text-ink-primary" data-work-order-field={field.label}>
                    {field.value}
                    {field.muted ? <span className="text-muted"> · {field.muted}</span> : null}
                  </dd>
                </div>
              ))}
            </dl>
            {handoff.tarpLine ? <p className="text-[11.5px] leading-snug text-muted" data-stop-tarp>{handoff.tarpLine}</p> : null}
            <p className="text-[11px] leading-snug text-muted" data-action-gate-recovery><NoBreakIds text={handoff.recoveryLine} /></p>
            {approval ? (
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" disabled className={cn(GATE_PRIMARY_BUTTON, 'h-8 disabled:opacity-100')}>Approved</button>
                <p className="text-[11px] leading-snug text-ink-secondary" data-approval-record>{approval.reason ?? decision?.reason}</p>
              </div>
            ) : decision ? (
              <p data-approval-record>{decision.kind} · {decision.approver} · {decision.reason}</p>
            ) : (
              <ActionGateControls reason={reason} onReason={setReason} onDecision={decide} seat={read.owner} canDecide={canDecide} cursorLabel={state.cursor.slice(11, 16)} />
            )}
            {handoff.stopDecision ? (
              <div className="flex flex-wrap items-center gap-1.5 border-t border-[var(--color-divider)] pt-2 text-[11.5px] text-muted" data-stop-decision>
                <span className={cn(KICKER, 'mr-0.5')}>Stop</span>
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <ActorChip actor="Human">{handoff.stopDecision.decides}</ActorChip>
                  decides
                </span>
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <ActorChip actor="Human">{handoff.stopDecision.confirms}</ActorChip>
                  confirms
                </span>
                {approval ? (
                  <span
                    className="inline-flex items-center whitespace-nowrap rounded-full bg-[color-mix(in_srgb,var(--state-blocked)_10%,transparent)] px-2 py-px text-[10px] font-semibold uppercase leading-4 tracking-[0.05em] text-[var(--state-blocked)]"
                    data-stop-escalation
                  >
                    {handoff.stopDecision.escalation}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </HandoffPanel>
      </section>
    </Commentable>
  );
}
